import { useEffect, useMemo } from "react";

import PropTypes from "prop-types";

import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Grid, Button } from "@mui/material";

import { useAsync } from "../../../hooks/useAsync";

import LoaderWrapper from "../../../components/wrapper/LoaderWrapper";

import { useForm } from "../../../hooks/useForm";

import useFilterSearchParams from "../../../hooks/useFilterSearchParams";
import useTicketService from "../../../services/ticketService";

import UniversalFilterForm from "../../form/UniversalFilterForm";
import TicketChartToolBarForm from "./TicketChartToolBarForm";

import useDialog from "../../../hooks/useDialog";
import UserFilterDialog from "../../dialog/UserFilterDialog";

import {
  getSearchParamsObjectForFilterPrefix,
  getFilterSearchParamsKeyForField,
  isEmptyArray,
  isEmptyValue,
} from "../../../helpers/methods";

import FilterAltIcon from "@mui/icons-material/FilterAlt";

import {
  overflowButtonStyle,
  centerVericalAlignIconStyle,
} from "../../../helpers/styles";

const UserFilteringDialogForm = (props) => {
  return (
    <>
      <TicketChartToolBarForm
        {...props}
        onlyFormContent={false}
        isVerticalForm={true}
        filteringData={props.filteringData}
        showSelectCategoryButton={props.showSelectCategoryButton}
        showSelectTimeRange={props.showSelectTimeRange}
        showSelectObjectLevel2={props.showSelectObjectLevel2}
        showTicketOwner={props.showTicketOwner}
        showClearFiltersIcon={props.showClearFiltersIcon}
        showOnlySelectDay={props.showOnlySelectDay}
        showOpenCloseFilterButton={props.showOpenCloseFilterButton}
        showTenantVisibilityButton={props.showTenantVisibilityButton}
        showSelectTicketK={props.showSelectTicketK}
        formValue={props.formValue}
      />
      <UniversalFilterForm
        {...props}
        filteringData={props.tableFilteringData}
        filtersConfig={props.tableFieldConfigs}
        style={{ marginTop: "4px" }}
      />
    </>
  );
};

export default function TicketChartToolBar(props) {
  const { t } = useTranslation();

  const [
    openUserFiltersDialog,
    onOpenUserFiltersDialog,
    onCloseUserFiltersDialog,
  ] = useDialog();

  const { getTicketChartFilteringData } = useTicketService();

  const filteringData = useAsync(getTicketChartFilteringData);

  const [searchParams, setSearchParams] = useSearchParams();
  const { getSearchParamsByFilterPrefix, clearSearchParamsByFilterPrefix } =
    useFilterSearchParams(searchParams);

  const {
    formValue,
    setFormValue,
    onChangeDateConvertedToDateTimeWithEndHourOfDate,
    onChangeDateConvertedToDateTimeWithZeroHourOfDate,
    onChangeDate,
    onChangeAutocompleteFieldWithObjectOptions,
  } = useForm(
    getSearchParamsByFilterPrefix(props.filterPrefix, ["time_range"])
  );

  useEffect(() => {
    setFormValue(
      getSearchParamsByFilterPrefix(props.filterPrefix, ["time_range"])
    );
  }, [searchParams]);

  const handleSearch = () => {
    setSearchParams(() => ({
      ...clearSearchParamsByFilterPrefix(props.filterPrefix),
      ...getSearchParamsObjectForFilterPrefix(formValue, props.filterPrefix),
      [getFilterSearchParamsKeyForField("page", props.filterPrefix)]: 1,
    }));
  };
  const handleCleanFilters = () => {
    setFormValue(() => ({}));
    setSearchParams(() => clearSearchParamsByFilterPrefix(props.filterPrefix));
  };

  const isLoading = formValue === undefined || !filteringData.value;

  const isFormValid = () => {
    let requiredFields = [];

    if (props.showSelectTimeRange) {
      requiredFields.push("time_range");

      if (!formValue.time_range || formValue.time_range === "date_range") {
        requiredFields = requiredFields.concat([
          "start_settlement_range",
          "end_settlement_range",
        ]);
      } else if (formValue.time_range === "day") {
        requiredFields.push("day");
      } else if (formValue.time_range === "month") {
        requiredFields.push("month");
      }
    }

    if (props.showTicketOwner) {
      requiredFields.push("ticket_owner_full_name");
    }

    return requiredFields.every((field) =>
      Array.isArray(formValue[field])
        ? !isEmptyArray(formValue[field])
        : !isEmptyValue(formValue[field])
    );
  };

  const excludedFieldsForUserFilterDialog = useMemo(() => {
    let excludedFields = ["created"];

    if (props.showSelectTicketK) {
      excludedFields.push("ticket_k");
    }

    if (props.showSelectCategoryButton) {
      excludedFields.push("ticket_category");
    }

    if (props.showSelectObjectLevel2) {
      excludedFields.push("objects_level_2_id");
    }

    if (props.showTicketOwner) {
      excludedFields.push("ticket_owner_full_name");
    }

    return excludedFields;
  }, [
    props.showSelectTicketK,
    props.showSelectCategoryButton,
    props.showSelectObjectLevel2,
    props.showTicketOwner,
  ]);

  const tableFiledsConfigForUserFilteringDialog = useMemo(
    () =>
      props.tableFieldConfigs.filter(
        (config) => !excludedFieldsForUserFilterDialog.includes(config.name)
      ),

    [props.tableFieldConfigs, excludedFieldsForUserFilterDialog]
  );

  return (
    <LoaderWrapper showLoader={isLoading}>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        columnSpacing={3}
        rowSpacing={2}
        marginY={1}
      >
        {props.showMyFiltersButton && (
          <Grid item xs={12} md={1.5}>
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={<FilterAltIcon sx={centerVericalAlignIconStyle} />}
              onClick={onOpenUserFiltersDialog}
              color={"primary"}
              sx={overflowButtonStyle}
            >
              {t("bar.tool_bar.my_filters")}
            </Button>
          </Grid>
        )}
        <TicketChartToolBarForm
          onlyFormContent={true}
          formValue={formValue}
          setFormValue={setFormValue}
          onChangeDateConvertedToDateTimeWithEndHourOfDate={
            onChangeDateConvertedToDateTimeWithEndHourOfDate
          }
          onChangeDateConvertedToDateTimeWithZeroHourOfDate={
            onChangeDateConvertedToDateTimeWithZeroHourOfDate
          }
          onChangeDate={onChangeDate}
          onChangeAutocompleteFieldWithObjectOptions={
            onChangeAutocompleteFieldWithObjectOptions
          }
          isVerticalForm={false}
          filteringData={filteringData}
          showSelectCategoryButton={props.showSelectCategoryButton}
          showSelectTimeRange={props.showSelectTimeRange}
          showSelectObjectLevel2={props.showSelectObjectLevel2}
          showTicketOwner={props.showTicketOwner}
          showSelectTicketK={props.showSelectTicketK}
          showClearFiltersIcon={props.showClearFiltersIcon}
          showOnlySelectDay={props.showOnlySelectDay}
        />

        <Grid
          container
          columnSpacing={2}
          item
          xs={12}
          md={props.showClearFiltersIcon ? 2.5 : 1.5}
          justifyContent={"flex-end"}
        >
          <Grid item xs={12} md={props.showClearFiltersIcon ? 5 : 12}>
            <Button
              variant="contained"
              color="success"
              size="small"
              fullWidth
              disabled={!isFormValid()}
              onClick={handleSearch}
            >
              {t("search")}
            </Button>
          </Grid>
          {props.showClearFiltersIcon && (
            <Grid item xs={12} md={7}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                fullWidth
                onClick={handleCleanFilters}
              >
                {t("clean_filters")}
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
      {openUserFiltersDialog && (
        <UserFilterDialog
          open={openUserFiltersDialog}
          onClose={onCloseUserFiltersDialog}
          pageName={props.pageName}
          filterPrefix={props.filterPrefix}
          filterForm={
            <UserFilteringDialogForm
              {...props}
              filteringData={filteringData}
              tableFieldConfigs={tableFiledsConfigForUserFilteringDialog}
            />
          }
        />
      )}
    </LoaderWrapper>
  );
}

TicketChartToolBar.propTypes = {
  pageName: PropTypes.string,
  filterPrefix: PropTypes.string,
  showMyFiltersButton: PropTypes.bool,
  showSelectCategoryButton: PropTypes.bool,
  showSelectTimeRange: PropTypes.bool,
  showSelectObjectLevel2: PropTypes.bool,
  showTicketOwner: PropTypes.bool,
  showClearFiltersIcon: PropTypes.bool,
  showOnlySelectDay: PropTypes.bool,
  showSelectTicketK: PropTypes.bool,
  tableFieldConfigs: PropTypes.object,
  tableFilteringData: PropTypes.object,
};

TicketChartToolBar.defaultProps = {
  showMyFiltersButton: true,
  showSelectCategoryButton: true,
  showSelectTimeRange: false,
  showSelectObjectLevel2: false,
  showTicketOwner: false,
  showClearFiltersIcon: true,
  showOnlySelectDay: false,
  showSelectTicketK: false,
};
